import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { serializeComment } from "components/comments/utils";
import { BasicComment } from "../../../lib/generated/app-service-gql/graphql";
import { updateBidQuery } from "../../../lib/hooks/api/bid/bids/updateBidCache";
import { useAssertCurrentUser } from "../../../lib/hooks/api/users/useCurrentUser";
import { CommentObjectType } from "../../../lib/types/graphQLEnums";
import { CommentDto } from "../../comments/CommentComponent";
import CommentsSidebar from "../../comments/CommentsSidebar";

type Props = {
  bidId: string;
  bidName: string;
  comments: CommentDto[];
};

export default function BidCommentsSidebar({ bidId, bidName, comments }: Props) {
  const queryClient = useQueryClient();
  const currentUser = useAssertCurrentUser();

  const onCreateComment = useCallback(
    (newComment: BasicComment) =>
      updateBidQuery(queryClient, bidId, (bid) => ({
        ...bid,
        comments: [serializeComment(newComment, currentUser), ...bid.comments],
      })),
    [currentUser, bidId, queryClient],
  );

  const onUpdateComment = useCallback(
    (updatedComment: BasicComment) =>
      updateBidQuery(queryClient, bidId, (bid) => ({
        ...bid,
        comments: bid.comments.map((comment) =>
          comment.id === updatedComment.id
            ? serializeComment(updatedComment, currentUser)
            : comment,
        ),
      })),
    [currentUser, bidId, queryClient],
  );

  const onDeleteComment = useCallback(
    (deletedCommentId: string) =>
      updateBidQuery(queryClient, bidId, (bid) => ({
        ...bid,
        comments: bid.comments.filter((comment) => comment.id !== deletedCommentId),
      })),
    [bidId, queryClient],
  );

  return (
    <CommentsSidebar
      objectId={bidId}
      objectType={CommentObjectType.Bid}
      objectName={bidName}
      comments={comments}
      onCreateComment={onCreateComment}
      onUpdateComment={onUpdateComment}
      onDeleteComment={onDeleteComment}
    />
  );
}
